<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import LiaAgentsAPI from 'dashboard/api/liaAgents';

// Tab Components
import TabStart from '../components/edit/TabStart.vue';
import TabPrompt from '../components/edit/TabPrompt.vue';
import TabSettings from '../components/edit/TabSettings.vue';
import TabInboxes from '../components/edit/TabInboxes.vue';
import TabTestMode from '../components/edit/TabTestMode.vue';
import TabKnowledge from '../components/edit/TabKnowledge.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

// Current account
const currentAccount = computed(() => store.getters.getCurrentAccount);

const agentId = computed(() => route.params.agentId);

const isLoading = ref(true);
const isSaving = ref(false);
const isToggling = ref(false);
const isRedeploying = ref(false);
const isEditingName = ref(false);
const nameInputRef = ref(null);
const activeTab = ref('start');

watch(isEditingName, val => {
  if (val) {
    nextTick(() => nameInputRef.value?.focus());
  }
});
const redeployDialogRef = ref(null);
const redeployPassword = ref('');
const integrationDialogRef = ref(null);
const integrationPassword = ref('');
const integrationUnlocked = ref(false);
const naviDialogRef = ref(null);
const naviPassword = ref('');
const naviUnlocked = ref(false);

const agent = ref(null);

// Agent Config
const agentConfig = ref({
  name: '',
  company_name: '',
  system_prompt: '',
  model: 'gpt-4.1-mini',
  language: 'pt-BR',
  languages: ['pt-BR'],
  inbox_ids: [],
  lia_integration_id: null,
});

// Persona Config
const personaConfig = ref({
  avatar: 'bot',
  customAvatar: '',
  role: 'atendente',
  description: '',
  specialties: [],
});

// Behavior Config
const behaviorConfig = ref({
  emoji_usage: 'sometimes',
  formality: 'neutral',
  response_length: 'short',
  tone: 'friendly',
  use_customer_name: 'sometimes',
  identify_as_ai: 'when_asked',
  formatting: 'minimal',
  greeting_style: 'warm',
  greeting_message: '',
  farewell_style: 'helpful',
  farewell_message: '',
});

// ERP Config
const erpConfig = ref({
  provider: 'ixc',
  api_url: '',
  api_token: '',
  client_id: '',
  client_secret: '',
});

// External APIs Config
const externalApisConfig = ref({
  navi_api_url: '',
  navi_api_token: '',
});

// Modules Config
const modulesConfig = ref({
  transferencia: true,
  viabilidade: true,
  resolver_conversa: true,
});

// Teams Config (dynamic array format: [{ key: "vendas", team_id: 41 }])
const teamsConfig = ref([]);

// Company Data Config
const companyDataConfig = ref({
  name: '',
  phone: '',
  whatsapp: '',
  email: '',
  website: '',
  address: '',
  business_hours: '',
});

// Agent Prompts Config (for LIA 3.1 multi-agent)
const agentPromptsConfig = ref({
  triagem: '',
  vendas: '',
  financeiro: '',
  suporte: '',
});

// Test Mode Config
const testModeConfig = ref({
  enabled: false,
  phone_numbers: [],
});

// Workflow Agents Config (names for each specialized agent)
const workflowAgentsConfig = ref({
  triagem_name: 'Lia',
  triagem_avatar: 'woman-blonde',
  vendas_name: 'Anna',
  vendas_avatar: 'woman-brunette',
  financeiro_name: 'Valentina',
  financeiro_avatar: 'woman-redhead',
  suporte_name: 'Bianca',
  suporte_avatar: 'woman-black',
});

// Workflow Toggles (enable/disable features and tools)
const workflowToggles = ref({});

// Calendar Config (Google Calendar for LIA 2.1)
const calendarConfig = ref({
  google_calendar_id: '',
});

// Workflow Config (advanced AI settings)
const workflowConfig = ref({
  ai_model: 'gpt-4.1-mini',
  ai_model_vision: 'gpt-4.1-mini',
  context_window: 15,
  buffer_wait_seconds: 12,
  typing_delay_min: 3,
  typing_delay_max: 7,
  max_message_chunks: 3,
  restart_keyword: 'restart',
  restart_message: 'Conversa reiniciada com sucesso.',
  image_analysis_prompt: '',
  knowledge_top_k: 3,
});

// Check if agent is LIA 1.0
const isLia10 = computed(() => {
  const version = agent.value?.lia_version || '';
  return version.includes('1.0');
});

// Check if agent is LIA 2.1
const isLia21 = computed(() => {
  const version = agent.value?.lia_version || '';
  return version.includes('2.1');
});

// Check if agent is LIA 3.1
const isLia31 = computed(() => {
  const version = agent.value?.lia_version || '';
  return version.includes('3.1') || !!agent.value?.integration_provider;
});

// Check if MyCore
const isMyCore = computed(() => {
  const provider = agent.value?.integration_provider || '';
  const version = agent.value?.lia_version || '';
  return provider === 'mycore' || version.toLowerCase().includes('mycore');
});

// Check if transfer module enabled
const transferEnabled = computed(() => {
  const modules = agent.value?.settings?.modules || {};
  return modules.transferencia !== false;
});

// Tabs - all settings promoted to top-level
const tabs = computed(() => {
  const items = [
    {
      id: 'start',
      label: t('LIA_AGENTS.EDIT.TAB_START'),
      icon: 'i-lucide-home',
    },
  ];

  // Company data - always
  items.push({
    id: 'company',
    label: t('LIA_AGENTS.SETTINGS.TAB_COMPANY'),
    icon: 'i-lucide-building-2',
    settingsTab: 'company',
  });

  // Integrations (ERP) - only for LIA 3.1, unlocked via password
  if (isLia31.value && integrationUnlocked.value) {
    items.push({
      id: 'integrations',
      label: t('LIA_AGENTS.SETTINGS.TAB_INTEGRATIONS'),
      icon: 'i-lucide-plug',
      settingsTab: 'integrations',
    });
  }

  // API Navi - only shown when unlocked via gear icon
  if (!isMyCore.value && naviUnlocked.value) {
    items.push({
      id: 'navi',
      label: t('LIA_AGENTS.SETTINGS.TAB_NAVI'),
      icon: 'i-lucide-globe',
      settingsTab: 'navi',
    });
  }

  // Teams - for versions with transfer enabled (not LIA 1.0)
  if (transferEnabled.value && !isLia10.value) {
    items.push({
      id: 'teams',
      label: t('LIA_AGENTS.SETTINGS.TAB_TEAMS'),
      icon: 'i-lucide-users',
      settingsTab: 'teams',
    });
  }

  // Calendar - only for LIA 2.1
  if (isLia21.value) {
    items.push({
      id: 'calendar',
      label: t('LIA_AGENTS.SETTINGS.TAB_CALENDAR'),
      icon: 'i-lucide-calendar',
      settingsTab: 'calendar',
    });
  }

  items.push({
    id: 'inboxes',
    label: t('LIA_AGENTS.EDIT.TAB_INBOXES'),
    icon: 'i-lucide-inbox',
  });

  // Knowledge tab - not available for LIA 1.0
  if (!isLia10.value) {
    items.push({
      id: 'knowledge',
      label: t('LIA_AGENTS.EDIT.TAB_KNOWLEDGE'),
      icon: 'i-lucide-book-open',
    });
  }

  items.push({
    id: 'prompt',
    label: t('LIA_AGENTS.EDIT.TAB_PROMPT'),
    icon: 'i-lucide-message-square',
  });

  items.push({
    id: 'testmode',
    label: t('LIA_AGENTS.EDIT.TAB_TEST_MODE'),
    icon: 'i-lucide-flask-conical',
  });

  return items;
});

// Map active tab to settings sub-tab for TabSettings
const activeSettingsTab = computed(() => {
  const currentTab = tabs.value.find(tab => tab.id === activeTab.value);
  return currentTab?.settingsTab || null;
});

// Check if current tab is a settings tab
const isSettingsTab = computed(() => !!activeSettingsTab.value);

// Computed
const canActivate = computed(() => {
  return (
    agentConfig.value.name &&
    agentConfig.value.system_prompt &&
    agentConfig.value.inbox_ids.length > 0
  );
});

// Load configs from agent settings
const loadConfigsFromAgent = () => {
  if (!agent.value) return;

  // Persona
  if (agent.value.settings?.persona) {
    personaConfig.value = {
      ...personaConfig.value,
      ...agent.value.settings.persona,
    };
  }

  // Behavior
  if (agent.value.settings?.behavior) {
    behaviorConfig.value = {
      ...behaviorConfig.value,
      ...agent.value.settings.behavior,
    };
  }

  // Test Mode
  if (agent.value.test_mode || agent.value.settings?.test_mode) {
    const tm = agent.value.test_mode || agent.value.settings.test_mode;
    testModeConfig.value = {
      enabled: tm.enabled || false,
      phone_numbers: tm.phone_numbers || [],
    };
  }

  // ERP
  if (agent.value.integration_provider) {
    erpConfig.value.provider = agent.value.integration_provider;
  }
  if (agent.value.integration_credentials_masked) {
    const masked = agent.value.integration_credentials_masked;
    Object.keys(masked || {}).forEach(key => {
      if (masked[key] && key in erpConfig.value) {
        erpConfig.value[key] = masked[key];
      }
    });
  }

  // External APIs
  if (agent.value.settings?.external_apis) {
    externalApisConfig.value = {
      navi_api_url: agent.value.settings.external_apis.navi_api_url || '',
      navi_api_token: agent.value.settings.external_apis.navi_api_token || '',
    };
  }

  // Modules
  if (agent.value.settings?.modules) {
    modulesConfig.value = {
      transferencia: agent.value.settings.modules.transferencia !== false,
      viabilidade: agent.value.settings.modules.viabilidade !== false,
      resolver_conversa:
        agent.value.settings.modules.resolver_conversa !== false,
    };
  }

  // Teams (supports both new array and legacy object format)
  if (agent.value.settings?.teams) {
    const savedTeams = agent.value.settings.teams;
    if (Array.isArray(savedTeams)) {
      teamsConfig.value = savedTeams.map(item => ({
        key: item.key || '',
        team_id: item.team_id ? Number(item.team_id) : '',
      }));
    } else {
      // Migrate legacy object format to array
      teamsConfig.value = Object.entries(savedTeams)
        .filter(([k, v]) => k.endsWith('_id') && v)
        .map(([k, v]) => ({
          key: k.replace(/_id$/, ''),
          team_id: Number(v),
        }));
    }
  }

  // Company Data
  if (agent.value.settings?.company_data || agent.value.company_data) {
    const cd = agent.value.settings?.company_data || agent.value.company_data;
    companyDataConfig.value = {
      name: cd.name || agentConfig.value.company_name || '',
      phone: cd.phone || '',
      whatsapp: cd.whatsapp || '',
      email: cd.email || '',
      website: cd.website || '',
      address: cd.address || '',
      business_hours: cd.business_hours || '',
    };
  } else {
    // Default company name from agent
    companyDataConfig.value.name = agentConfig.value.company_name || '';
  }

  // Workflow Agents Names
  if (agent.value.settings?.workflow_agents) {
    const wa = agent.value.settings.workflow_agents;
    workflowAgentsConfig.value = {
      triagem_name: wa.triagem_name || 'Lia',
      triagem_avatar: wa.triagem_avatar || 'woman-blonde',
      vendas_name: wa.vendas_name || 'Anna',
      vendas_avatar: wa.vendas_avatar || 'woman-brunette',
      financeiro_name: wa.financeiro_name || 'Valentina',
      financeiro_avatar: wa.financeiro_avatar || 'woman-redhead',
      suporte_name: wa.suporte_name || 'Bianca',
      suporte_avatar: wa.suporte_avatar || 'woman-black',
    };
  }

  // Workflow Toggles
  if (agent.value.settings?.workflow_toggles) {
    workflowToggles.value = { ...agent.value.settings.workflow_toggles };
  }

  // Calendar Config (Google Calendar for LIA 2.1)
  if (agent.value.settings?.calendar) {
    const cal = agent.value.settings.calendar;
    calendarConfig.value = {
      google_calendar_id: cal.google_calendar_id || '',
    };
  }

  // Workflow Config (advanced AI settings)
  if (agent.value.settings?.workflow_config) {
    const wc = agent.value.settings.workflow_config;
    workflowConfig.value = {
      ai_model: wc.ai_model || 'gpt-4.1-mini',
      ai_model_vision: wc.ai_model_vision || 'gpt-4.1-mini',
      context_window: wc.context_window || 15,
      buffer_wait_seconds: wc.buffer_wait_seconds || 12,
      typing_delay_min: wc.typing_delay_min ?? 3,
      typing_delay_max: wc.typing_delay_max ?? 7,
      max_message_chunks: wc.max_message_chunks ?? 3,
      restart_keyword: wc.restart_keyword || 'restart',
      restart_message: wc.restart_message || 'Conversa reiniciada com sucesso.',
      image_analysis_prompt: wc.image_analysis_prompt || '',
      knowledge_top_k: wc.knowledge_top_k ?? 3,
    };
  }

  // Agent Prompts (for LIA 3.1)
  if (agent.value.settings?.agent_prompts) {
    const ap = agent.value.settings.agent_prompts;
    agentPromptsConfig.value = {
      triagem: ap.triagem || '',
      // Backward compat: if vendas has triagem content from old combined prompt, keep it
      vendas: ap.vendas || '',
      financeiro: ap.financeiro || '',
      suporte: ap.suporte || '',
    };
  }
};

// Fetch agent
const fetchAgent = async () => {
  isLoading.value = true;
  try {
    await store.dispatch('liaAgents/fetch');
    const agents = store.getters['liaAgents/getAgents'];
    agent.value = agents.find(a => a.id === Number(agentId.value));

    if (agent.value) {
      const storedLanguages =
        agent.value.settings?.languages ||
        (agent.value.language ? [agent.value.language] : ['pt-BR']);

      agentConfig.value = {
        name: agent.value.name || '',
        company_name:
          agent.value.company_name || currentAccount.value?.name || '',
        system_prompt: agent.value.system_prompt || '',
        model: agent.value.model || 'gpt-4.1-mini',
        language: agent.value.language || 'pt-BR',
        languages: storedLanguages,
        inbox_ids: agent.value.inbox_ids || [],
        lia_integration_id: agent.value.integration_id || null,
      };

      // Load configs from settings
      loadConfigsFromAgent();
    }
  } catch (error) {
    useAlert(t('LIA_AGENTS.EDIT.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchAgent();
  store.dispatch('inboxes/get');
  store.dispatch('liaIntegrations/fetch');
});

// Watch for account changes to auto-fill company name
watch(
  currentAccount,
  newAccount => {
    if (newAccount?.name && !agentConfig.value.company_name) {
      agentConfig.value.company_name = newAccount.name;
    }
  },
  { immediate: true }
);

// Navigation
const goBack = () => {
  router.push(accountScopedRoute('lia_agents_list'));
};

// Save agent
const saveAgent = async () => {
  isSaving.value = true;
  try {
    const currentSettings = agent.value?.settings || {};
    const updatedSettings = {
      ...currentSettings,
      languages: agentConfig.value.languages,
      behavior: behaviorConfig.value,
      persona: personaConfig.value,
      test_mode: testModeConfig.value,
      external_apis: externalApisConfig.value,
      modules: modulesConfig.value,
      teams: teamsConfig.value,
      workflow_agents: workflowAgentsConfig.value,
      agent_prompts: agentPromptsConfig.value,
      workflow_config: workflowConfig.value,
      workflow_toggles: workflowToggles.value,
      calendar: calendarConfig.value,
    };

    await store.dispatch('liaAgents/update', {
      id: agentId.value,
      ...agentConfig.value,
      settings: updatedSettings,
    });
    useAlert(t('LIA_AGENTS.EDIT.SAVE_SUCCESS'));
    await fetchAgent();
  } catch (error) {
    useAlert(error.message || t('LIA_AGENTS.EDIT.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

// Toggle activation
const toggleActivation = async () => {
  if (!agent.value) return;

  if (!agent.value.is_active) {
    if (!agentConfig.value.name || !agentConfig.value.system_prompt) {
      useAlert(t('LIA_AGENTS.EDIT.ACTIVATION_REQUIREMENTS'));
      return;
    }
    if (agentConfig.value.inbox_ids.length === 0) {
      useAlert(t('LIA_AGENTS.EDIT.ACTIVATION_INBOX_REQUIRED'));
      activeTab.value = 'inboxes';
      return;
    }
  }

  isToggling.value = true;
  try {
    if (agent.value.is_active) {
      await store.dispatch('liaAgents/deactivate', agent.value.id);
      useAlert(t('LIA_AGENTS.EDIT.DEACTIVATED'));
    } else {
      await store.dispatch('liaAgents/activate', agent.value.id);
      useAlert(t('LIA_AGENTS.EDIT.ACTIVATED'));
    }
    await fetchAgent();
  } catch (error) {
    useAlert(error.message || t('LIA_AGENTS.EDIT.TOGGLE_ERROR'));
  } finally {
    isToggling.value = false;
  }
};

// Redeploy workflow - requires admin password
const openRedeployDialog = () => {
  redeployPassword.value = '';
  redeployDialogRef.value?.open();
};

const confirmRedeploy = async () => {
  if (!redeployPassword.value) return;
  isRedeploying.value = true;
  try {
    const response = await LiaAgentsAPI.redeployWorkflow(
      agentId.value,
      redeployPassword.value
    );
    if (response.data.success) {
      useAlert(t('LIA_AGENTS.EDIT.REDEPLOY_SUCCESS'));
      redeployDialogRef.value?.close();
    } else {
      useAlert(
        t('LIA_AGENTS.EDIT.REDEPLOY_ERROR', { error: response.data.error })
      );
    }
  } catch (error) {
    const msg = error.response?.data?.error || error.message;
    useAlert(t('LIA_AGENTS.EDIT.REDEPLOY_ERROR', { error: msg }));
  } finally {
    isRedeploying.value = false;
    redeployPassword.value = '';
  }
};

// Integration unlock - requires password
const openIntegrationDialog = () => {
  integrationPassword.value = '';
  integrationDialogRef.value?.open();
};

const confirmIntegrationUnlock = async () => {
  if (!integrationPassword.value) return;
  try {
    const response = await LiaAgentsAPI.verifyPassword(
      agentId.value,
      integrationPassword.value
    );
    if (response.data.success) {
      integrationUnlocked.value = true;
      activeTab.value = 'integrations';
      integrationDialogRef.value?.close();
      useAlert('Integração desbloqueada');
    }
  } catch (error) {
    useAlert(error.response?.data?.error || 'Senha incorreta');
  } finally {
    integrationPassword.value = '';
  }
};

// Navi unlock - requires password
const openNaviDialog = () => {
  naviPassword.value = '';
  naviDialogRef.value?.open();
};

const confirmNaviUnlock = async () => {
  if (!naviPassword.value) return;
  try {
    const response = await LiaAgentsAPI.verifyPassword(
      agentId.value,
      naviPassword.value
    );
    if (response.data.success) {
      naviUnlocked.value = true;
      activeTab.value = 'navi';
      naviDialogRef.value?.close();
      useAlert('API Navi desbloqueada');
    }
  } catch (error) {
    useAlert(error.response?.data?.error || 'Senha incorreta');
  } finally {
    naviPassword.value = '';
  }
};

// Save external APIs config
const saveExternalApisConfig = async () => {
  const currentSettings = agent.value?.settings || {};
  const updatedSettings = {
    ...currentSettings,
    external_apis: {
      navi_api_url: externalApisConfig.value.navi_api_url,
      navi_api_token: externalApisConfig.value.navi_api_token,
    },
  };

  await store.dispatch('liaAgents/update', {
    id: agentId.value,
    settings: updatedSettings,
  });

  await fetchAgent();
};

// Save test mode from tab
const saveTestModeFromTab = async () => {
  const currentSettings = agent.value?.settings || {};
  const updatedSettings = {
    ...currentSettings,
    test_mode: {
      enabled: testModeConfig.value.enabled,
      phone_numbers: testModeConfig.value.phone_numbers,
    },
  };

  await store.dispatch('liaAgents/update', {
    id: agentId.value,
    settings: updatedSettings,
  });

  await fetchAgent();
};

// Save teams config
const saveTeamsConfig = async () => {
  const currentSettings = agent.value?.settings || {};
  const updatedSettings = {
    ...currentSettings,
    teams: teamsConfig.value,
  };

  await store.dispatch('liaAgents/update', {
    id: agentId.value,
    settings: updatedSettings,
  });

  await fetchAgent();
};

// Save company data config
const saveCompanyDataConfig = async () => {
  const currentSettings = agent.value?.settings || {};
  const updatedSettings = {
    ...currentSettings,
    company_data: companyDataConfig.value,
  };

  await store.dispatch('liaAgents/update', {
    id: agentId.value,
    settings: updatedSettings,
  });

  await fetchAgent();
};

// Save workflow toggles (enable/disable features and tools)
const saveWorkflowToggles = async () => {
  const currentSettings = agent.value?.settings || {};
  const updatedSettings = {
    ...currentSettings,
    workflow_toggles: workflowToggles.value,
  };

  await store.dispatch('liaAgents/update', {
    id: agentId.value,
    settings: updatedSettings,
  });

  await fetchAgent();
};

// Save calendar config
const saveCalendarConfig = async () => {
  const currentSettings = agent.value?.settings || {};
  const updatedSettings = {
    ...currentSettings,
    calendar: calendarConfig.value,
  };

  await store.dispatch('liaAgents/update', {
    id: agentId.value,
    settings: updatedSettings,
  });

  await fetchAgent();
};

// Save workflow config (advanced AI settings)
const saveWorkflowConfigSettings = async () => {
  const currentSettings = agent.value?.settings || {};
  const updatedSettings = {
    ...currentSettings,
    workflow_config: workflowConfig.value,
  };

  await store.dispatch('liaAgents/update', {
    id: agentId.value,
    settings: updatedSettings,
  });

  await fetchAgent();
};

// Save tools config (disabled_tools array)
const saveToolsConfig = async disabledTools => {
  const currentSettings = agent.value?.settings || {};
  const updatedSettings = {
    ...currentSettings,
    disabled_tools: disabledTools,
  };

  await store.dispatch('liaAgents/update', {
    id: agentId.value,
    settings: updatedSettings,
  });

  await fetchAgent();
};
</script>

<template>
  <div class="flex flex-col h-full bg-n-background">
    <!-- Header -->
    <header class="px-4 sm:px-6 py-4 border-b border-n-weak bg-n-solid-2">
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div class="flex items-center gap-3 sm:gap-4">
          <button
            type="button"
            class="p-2 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-lg transition-colors flex-shrink-0"
            @click="goBack"
          >
            <span class="i-lucide-arrow-left size-5" />
          </button>
          <div class="min-w-0">
            <div class="flex items-center gap-2 sm:gap-3 flex-wrap">
              <!-- Editable name -->
              <div
                v-if="agent"
                class="flex items-center gap-2 group/name cursor-pointer"
                @click="isEditingName = true"
              >
                <h1
                  v-show="!isEditingName"
                  class="text-lg sm:text-xl font-semibold text-n-slate-12 truncate"
                >
                  {{ agentConfig.name || t('LIA_AGENTS.EDIT.TITLE') }}
                </h1>
                <input
                  v-show="isEditingName"
                  ref="nameInputRef"
                  v-model="agentConfig.name"
                  type="text"
                  class="text-lg sm:text-xl font-semibold text-n-slate-12 bg-transparent outline-none border-none p-0 m-0 h-auto leading-[inherit]"
                  :style="{ width: (agentConfig.name.length || 10) + 1 + 'ch' }"
                  :placeholder="t('LIA_AGENTS.EDIT.TITLE')"
                  @blur="isEditingName = false"
                  @keyup.enter="isEditingName = false"
                />
                <span
                  v-show="!isEditingName"
                  class="i-lucide-pencil size-3.5 text-n-slate-9 opacity-0 group-hover/name:opacity-100 transition-opacity flex-shrink-0"
                />
              </div>
              <h1
                v-else
                class="text-lg sm:text-xl font-semibold text-n-slate-12 truncate"
              >
                {{ t('LIA_AGENTS.EDIT.TITLE') }}
              </h1>
              <span
                v-if="agent"
                class="px-2.5 py-0.5 text-xs font-medium rounded-full flex-shrink-0"
                :class="[
                  agent.is_active
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-n-alpha-2 text-n-slate-11',
                ]"
              >
                {{
                  agent.is_active
                    ? $t('LIA_AGENTS.EDIT.ACTIVE')
                    : $t('LIA_AGENTS.EDIT.INACTIVE')
                }}
              </span>
            </div>
            <!-- Subtitle: version, company, ERP -->
            <p
              v-if="agent"
              class="text-xs sm:text-sm text-n-slate-11 mt-0.5 truncate"
            >
              {{ agent.lia_version || 'LIA 2.1' }}
              <span v-if="agentConfig.company_name">
                · {{ agentConfig.company_name }}
              </span>
              <span v-if="agent.integration_name">
                · {{ agent.integration_name }}
              </span>
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <!-- API Navi Settings Button (gear icon) -->
          <button
            v-if="agent && !isMyCore"
            type="button"
            class="flex items-center justify-center p-2 rounded-lg transition-colors"
            :class="
              naviUnlocked
                ? 'text-green-600 bg-green-50 dark:bg-green-900/20'
                : 'text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3'
            "
            title="API Navi"
            @click="naviUnlocked ? (activeTab = 'navi') : openNaviDialog()"
          >
            <span class="i-lucide-globe size-4" />
          </button>
          <!-- Integration Settings Button (gear icon) -->
          <button
            v-if="agent && isLia31"
            type="button"
            class="flex items-center justify-center p-2 rounded-lg transition-colors"
            :class="
              integrationUnlocked
                ? 'text-green-600 bg-green-50 dark:bg-green-900/20'
                : 'text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3'
            "
            title="Configurações de Integração (ERP)"
            @click="
              integrationUnlocked
                ? (activeTab = 'integrations')
                : openIntegrationDialog()
            "
          >
            <span class="i-lucide-settings size-4" />
          </button>
          <!-- Redeploy Button -->
          <button
            v-if="agent"
            type="button"
            class="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors flex-1 sm:flex-initial text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/40"
            :disabled="isRedeploying"
            :title="$t('LIA_AGENTS.EDIT.REDEPLOY_TOOLTIP')"
            @click="openRedeployDialog"
          >
            <span
              v-if="isRedeploying"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-refresh-cw size-4" />
            <span class="hidden sm:inline">{{
              $t('LIA_AGENTS.EDIT.REDEPLOY')
            }}</span>
          </button>
          <!-- Activate/Deactivate -->
          <button
            v-if="agent"
            type="button"
            class="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors flex-1 sm:flex-initial"
            :class="[
              agent.is_active
                ? 'text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3'
                : 'text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed',
            ]"
            :disabled="isToggling || (!agent.is_active && !canActivate)"
            @click="toggleActivation"
          >
            <span
              v-if="isToggling"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span
              v-else
              :class="agent.is_active ? 'i-lucide-pause' : 'i-lucide-play'"
              class="size-4"
            />
            <span class="hidden sm:inline">{{
              agent.is_active
                ? $t('LIA_AGENTS.EDIT.DEACTIVATE')
                : $t('LIA_AGENTS.EDIT.ACTIVATE')
            }}</span>
          </button>
          <!-- Save Button -->
          <button
            type="button"
            class="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-initial"
            :disabled="isSaving"
            @click="saveAgent"
          >
            <span
              v-if="isSaving"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-save size-4" />
            <span class="hidden sm:inline">{{
              $t('LIA_AGENTS.EDIT.SAVE')
            }}</span>
          </button>
        </div>
      </div>

      <!-- Tabs -->
      <div
        class="flex gap-1 mt-4 overflow-x-auto no-scrollbar -mx-4 sm:mx-0 px-4 sm:px-0"
      >
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex-shrink-0"
          :class="[
            activeTab === tab.id
              ? 'bg-woot-500 text-white'
              : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          @click="activeTab = tab.id"
        >
          <span :class="tab.icon" class="size-4" />
          <span class="hidden sm:inline">{{ tab.label }}</span>
        </button>
      </div>
    </header>

    <!-- Loading -->
    <div v-if="isLoading" class="flex-1 flex items-center justify-center">
      <span class="i-lucide-loader-2 size-8 animate-spin text-n-slate-11" />
    </div>

    <!-- Content -->
    <div v-else class="flex-1 overflow-auto p-4 sm:p-6">
      <div class="w-full">
        <!-- Activation Warning -->
        <div
          v-if="!agent?.is_active && !canActivate"
          class="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl"
        >
          <div class="flex items-start gap-3">
            <span
              class="i-lucide-alert-triangle size-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5"
            />
            <div>
              <p
                class="text-sm font-medium text-yellow-800 dark:text-yellow-300"
              >
                {{ $t('LIA_AGENTS.EDIT.CONFIGURE_TO_ACTIVATE') }}
              </p>
              <ul
                class="mt-1 text-xs text-yellow-700 dark:text-yellow-400 space-y-0.5"
              >
                <li v-if="!agentConfig.name" class="flex items-center gap-1">
                  <span class="i-lucide-x size-3" />
                  {{ $t('LIA_AGENTS.EDIT.AGENT_NAME_REQUIRED') }}
                </li>
                <li
                  v-if="!agentConfig.system_prompt"
                  class="flex items-center gap-1"
                >
                  <span class="i-lucide-x size-3" />
                  {{ $t('LIA_AGENTS.EDIT.SYSTEM_PROMPT_REQUIRED') }}
                </li>
                <li
                  v-if="agentConfig.inbox_ids.length === 0"
                  class="flex items-center gap-1"
                >
                  <span class="i-lucide-x size-3" />
                  {{ $t('LIA_AGENTS.EDIT.SELECT_INBOX') }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Active Status Card -->
        <div
          v-if="agent?.is_active"
          class="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center"
            >
              <span
                class="i-lucide-check-circle size-5 text-green-600 dark:text-green-400"
              />
            </div>
            <div>
              <p class="text-sm font-medium text-green-800 dark:text-green-300">
                {{ $t('LIA_AGENTS.EDIT.AGENT_ACTIVE_RESPONDING') }}
              </p>
              <p class="text-xs text-green-700 dark:text-green-400">
                {{
                  $t('LIA_AGENTS.EDIT.SERVING_INBOXES', {
                    count: agentConfig.inbox_ids.length,
                  })
                }}
              </p>
            </div>
          </div>
        </div>

        <!-- Tab: Start -->
        <TabStart
          v-if="activeTab === 'start'"
          :agent-config="agentConfig"
          :persona-config="personaConfig"
          :behavior-config="behaviorConfig"
          :workflow-agents-config="workflowAgentsConfig"
          :test-mode-enabled="testModeConfig.enabled"
          :agent="agent"
          @update:agent-config="agentConfig = $event"
          @update:persona-config="personaConfig = $event"
          @update:workflow-agents-config="workflowAgentsConfig = $event"
        />

        <!-- Tab: Prompt -->
        <TabPrompt
          v-if="activeTab === 'prompt'"
          v-model="agentConfig.system_prompt"
          :company-name="agentConfig.company_name"
          :agent="agent"
          :workflow-agents-config="workflowAgentsConfig"
          :behavior-config="behaviorConfig"
          :company-data-config="companyDataConfig"
          :persona-config="personaConfig"
          :agent-prompts-config="agentPromptsConfig"
          @update:agent-prompts-config="agentPromptsConfig = $event"
        />

        <!-- Settings Tabs (Integrations, Company, Teams, Controls, Advanced) -->
        <TabSettings
          v-if="isSettingsTab"
          :agent="agent"
          :erp-config="erpConfig"
          :external-apis-config="externalApisConfig"
          :company-data-config="companyDataConfig"
          :teams-config="teamsConfig"
          :workflow-config="workflowConfig"
          :workflow-toggles="workflowToggles"
          :calendar-config="calendarConfig"
          :initial-tab="activeSettingsTab"
          @update:erp-config="erpConfig = $event"
          @update:external-apis-config="externalApisConfig = $event"
          @update:company-data-config="companyDataConfig = $event"
          @update:teams-config="teamsConfig = $event"
          @update:workflow-config="workflowConfig = $event"
          @update:workflow-toggles="workflowToggles = $event"
          @update:calendar-config="calendarConfig = $event"
          @save-external-apis="saveExternalApisConfig"
          @save-company-data="saveCompanyDataConfig"
          @save-tools="saveToolsConfig"
          @save-teams="saveTeamsConfig"
          @save-workflow-config="saveWorkflowConfigSettings"
          @save-workflow-toggles="saveWorkflowToggles"
          @save-calendar="saveCalendarConfig"
          @refresh="fetchAgent"
        />

        <!-- Tab: Inboxes -->
        <TabInboxes
          v-if="activeTab === 'inboxes'"
          v-model="agentConfig.inbox_ids"
          :single-select="isLia10"
        />

        <!-- Tab: Knowledge Base -->
        <TabKnowledge v-if="activeTab === 'knowledge'" :agent="agent" />

        <!-- Tab: Test Mode -->
        <TabTestMode
          v-if="activeTab === 'testmode'"
          v-model="testModeConfig"
          :agent="agent"
          @save="saveTestModeFromTab"
          @refresh="fetchAgent"
        />
      </div>
    </div>

    <!-- API Navi Unlock Dialog -->
    <Dialog
      ref="naviDialogRef"
      type="alert"
      title="API Navi"
      description="Digite sua senha de administrador para acessar as configurações da API Navi."
      confirm-button-label="Desbloquear"
      :cancel-button-label="$t('DIALOG.BUTTONS.CANCEL')"
      :disable-confirm-button="!naviPassword"
      width="sm"
      @confirm="confirmNaviUnlock"
    >
      <div class="flex flex-col gap-2">
        <label for="navi-password" class="text-sm font-medium text-n-slate-12">
          Senha do Administrador
        </label>
        <input
          id="navi-password"
          v-model="naviPassword"
          type="password"
          placeholder="Digite sua senha"
          class="w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          autocomplete="current-password"
          @keyup.enter="confirmNaviUnlock"
        />
      </div>
    </Dialog>

    <!-- Integration Unlock Dialog -->
    <Dialog
      ref="integrationDialogRef"
      type="alert"
      title="Configurações de Integração"
      description="Digite sua senha de administrador para acessar as configurações de integração (ERP). Alterações incorretas podem afetar o funcionamento do agente."
      confirm-button-label="Desbloquear"
      :cancel-button-label="$t('DIALOG.BUTTONS.CANCEL')"
      :disable-confirm-button="!integrationPassword"
      width="sm"
      @confirm="confirmIntegrationUnlock"
    >
      <div class="flex flex-col gap-2">
        <label
          for="integration-password"
          class="text-sm font-medium text-n-slate-12"
        >
          Senha do Administrador
        </label>
        <input
          id="integration-password"
          v-model="integrationPassword"
          type="password"
          placeholder="Digite sua senha"
          class="w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          autocomplete="current-password"
          @keyup.enter="confirmIntegrationUnlock"
        />
      </div>
    </Dialog>

    <!-- Redeploy Confirmation Dialog -->
    <Dialog
      ref="redeployDialogRef"
      type="alert"
      :title="$t('LIA_AGENTS.EDIT.REDEPLOY_CONFIRM_TITLE')"
      :description="$t('LIA_AGENTS.EDIT.REDEPLOY_CONFIRM_DESC')"
      :confirm-button-label="$t('LIA_AGENTS.EDIT.REDEPLOY')"
      :cancel-button-label="$t('DIALOG.BUTTONS.CANCEL')"
      :is-loading="isRedeploying"
      :disable-confirm-button="!redeployPassword"
      width="sm"
      @confirm="confirmRedeploy"
    >
      <div class="flex flex-col gap-2">
        <label
          for="redeploy-password"
          class="text-sm font-medium text-n-slate-12"
        >
          {{ $t('LIA_AGENTS.EDIT.REDEPLOY_PASSWORD_LABEL') }}
        </label>
        <input
          id="redeploy-password"
          v-model="redeployPassword"
          type="password"
          :placeholder="$t('LIA_AGENTS.EDIT.REDEPLOY_PASSWORD_PLACEHOLDER')"
          class="w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          autocomplete="current-password"
          @keyup.enter="confirmRedeploy"
        />
      </div>
    </Dialog>
  </div>
</template>
